/**
 * Utility functions for encryption
 * 
 * Uses node-forge for RSA encryption with PKCS1_OAEP padding
 * to match server-side decryption requirements.
 */
import forge from 'node-forge';

/**
 * Normalize and validate public key format
 * @param {string} publicKey - Raw public key string
 * @returns {string} Normalized public key
 */
const normalizePublicKey = (publicKey) => {
  if (!publicKey || typeof publicKey !== 'string') {
    throw new Error('Public key is empty or invalid');
  }

  // Remove any extra whitespace and normalize line breaks
  let normalized = publicKey.trim();
  
  // Ensure proper line breaks
  normalized = normalized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Check if it's already in PEM format
  if (normalized.includes('-----BEGIN')) {
    return normalized;
  }
  
  // If it's just the key content without headers, try to add them
  // This handles keys that might be missing the PEM headers
  const keyContent = normalized.replace(/-----BEGIN[^-]+-----/g, '')
                                .replace(/-----END[^-]+-----/g, '')
                                .replace(/\s/g, '');
  
  if (keyContent.length === 0) {
    throw new Error('Public key content is empty');
  }
  
  // Try to determine if it's PKCS#1 or PKCS#8 format based on length
  // PKCS#1 keys are typically shorter, PKCS#8 are longer
  // For JSEncrypt, we'll try PKCS#8 format first (most common)
  return `-----BEGIN PUBLIC KEY-----\n${keyContent}\n-----END PUBLIC KEY-----`;
};

/**
 * Load public key from PEM file
 * @param {string} publicKeyPath - Path to the PEM file (relative to public folder)
 * @returns {Promise<string>} Public key as string
 */
export const loadPublicKey = async (publicKeyPath = '/public.pem') => {
  try {
    const response = await fetch(publicKeyPath);
    if (!response.ok) {
      throw new Error(`Failed to load public key: ${response.statusText} (Status: ${response.status})`);
    }
    const publicKey = await response.text();
    
    if (!publicKey || publicKey.trim().length === 0) {
      throw new Error('Public key file is empty');
    }
    
    return normalizePublicKey(publicKey);
  } catch (error) {
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Failed to load public key file from ${publicKeyPath}. Please ensure the file exists in the public folder.`);
    }
    throw error;
  }
};


/**
 * Encrypt password using RSA public key with PKCS1_OAEP padding
 * @param {string} password - Plain text password
 * @param {string} publicKey - RSA public key in PEM format
 * @returns {string} Encrypted password (base64 encoded)
 */
export const encryptPassword = (password, publicKey) => {
  if (!password || typeof password !== 'string') {
    throw new Error('Password is required and must be a string');
  }
  
  if (!publicKey || typeof publicKey !== 'string') {
    throw new Error('Public key is required and must be a string');
  }

  try {
    // Clean the public key - ensure proper formatting
    let cleanedKey = publicKey.trim();
    
    // Normalize line breaks
    cleanedKey = cleanedKey.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Parse the public key using node-forge
    let publicKeyPem;
    try {
      // Try to parse as PEM format
      publicKeyPem = forge.pki.publicKeyFromPem(cleanedKey);
    } catch (parseError) {
      // If parsing fails, try to fix the format
      // Ensure proper PEM formatting
      if (!cleanedKey.includes('-----BEGIN')) {
        throw new Error('Public key must be in PEM format with BEGIN/END headers');
      }
      
      // Try parsing again with cleaned key
      publicKeyPem = forge.pki.publicKeyFromPem(cleanedKey);
    }
    
    // Check key size
    const keySize = publicKeyPem.n.bitLength();
    
    // Check if password is too long (RSA OAEP has message size limits)
    // For OAEP with SHA-1, max message size is keySize/8 - 2*20 - 2 bytes
    // For 2048-bit key: 256 - 40 - 2 = 214 bytes
    const passwordBytes = new TextEncoder().encode(password).length;
    const maxMessageSize = Math.floor(keySize / 8) - 42; // Conservative estimate for OAEP
    
    if (passwordBytes > maxMessageSize) {
      throw new Error(`Password is too long (${passwordBytes} bytes). Maximum is approximately ${maxMessageSize} bytes for ${keySize}-bit RSA key with OAEP padding.`);
    }
    
    // Convert password to bytes
    const passwordBytesArray = forge.util.encodeUtf8(password);
    
    // Encrypt using OAEP padding (PKCS1_OAEP)
    // OAEP uses SHA-1 by default in node-forge
    const encrypted = publicKeyPem.encrypt(passwordBytesArray, 'RSA-OAEP', {
      md: forge.md.sha1.create(),
      mgf1: {
        md: forge.md.sha1.create()
      }
    });
    
    // Convert to base64
    const encryptedBase64 = forge.util.encode64(encrypted);
    
    return encryptedBase64;
  } catch (error) {
    // Provide more helpful error messages
    if (error.message.includes('Invalid') || error.message.includes('format')) {
      throw error;
    }
    if (error.message.includes('too long')) {
      throw error;
    }
    throw new Error(`Failed to encrypt password: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Encrypt password using public key from file
 * @param {string} password - Plain text password
 * @param {string} publicKeyPath - Path to the PEM file (default: '/public.pem')
 * @returns {Promise<string>} Encrypted password (base64 encoded)
 */
export const encryptPasswordWithKeyFile = async (password, publicKeyPath = '/public.pem') => {
  try {
    const publicKey = await loadPublicKey(publicKeyPath);
    return encryptPassword(password, publicKey);
  } catch (error) {
    // Re-throw with more context
    if (error.message.includes('Failed to load')) {
      throw new Error(`Failed to load public key from ${publicKeyPath}. ${error.message}`);
    }
    throw error;
  }
};

